import React from "react";

/**
 * <ExplorerAvatar>
 *
 * Renders a user's profile picture with an optional gold ring for
 * grandfathered founders (WildQuest v1 supporters, `is_founder=true`).
 *
 * The ring is deliberately subtle — a 2px double-gold gradient border
 * with a soft outer glow — so it reads as a badge of honour, not a
 * decoration. Non-founders get the normal glass border used elsewhere
 * in the app.
 *
 * Sizes are Tailwind width/height classes so callers can size it
 * inline (e.g. size="w-9 h-9"). The ring scales with the avatar.
 */
export const ExplorerAvatar = ({
  src,
  name = "",
  founder = false,
  size = "w-9 h-9",
  className = "",
  ...rest
}) => {
  const fallback = `https://i.pravatar.cc/80?u=${encodeURIComponent(name || "explorer")}`;
  const url = src || fallback;

  if (!founder) {
    return (
      <img
        src={url}
        alt={name}
        data-testid="explorer-avatar"
        className={`${size} rounded-full object-cover border border-white/10 ${className}`}
        {...rest}
      />
    );
  }

  // Founder — subtle gold ring + faint outer glow.
  return (
    <span
      data-testid="explorer-avatar-founder"
      title={`${name || "Explorer"} · Founder`}
      className={`${size} relative inline-block rounded-full ${className}`}
      style={{
        padding: 2,
        background:
          "conic-gradient(from 210deg, #E9C46A 0deg, #F9D976 90deg, #B8860B 180deg, #F9D976 270deg, #E9C46A 360deg)",
        boxShadow: "0 0 0 1px rgba(233,196,106,0.35), 0 0 10px rgba(233,196,106,0.35)",
      }}
      {...rest}
    >
      <img
        src={url}
        alt={name}
        className="w-full h-full rounded-full object-cover block"
        style={{ background: "#040A08" }}
      />
    </span>
  );
};

export default ExplorerAvatar;
